import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import {
  useBillingReconciliationLifecycleAlerts,
  useUpdateBillingReconciliationLifecycleAlert,
} from '../../hooks/api/useBillingReconciliationHooks.js';
import { alertDealerName, lifecycleAlertLabel, severityColor, severityRank } from './LifecycleAlertsPanel.jsx';

const sortedAlerts = (alerts = []) => [...alerts].sort((left, right) => (
  severityRank(left.severity) - severityRank(right.severity)
  || String(left.code || '').localeCompare(String(right.code || ''))
  || String(left.id || '').localeCompare(String(right.id || ''))
));

const LifecycleAlertsDrawerSummary = ({ runId, bac, context = null, prefetchedAlerts = null }) => {
  const hasPrefetchedAlerts = Array.isArray(prefetchedAlerts);
  const query = useBillingReconciliationLifecycleAlerts(
    { runId, bac, dismissed: false, limit: 100 },
    { enabled: Boolean(runId && bac) && !hasPrefetchedAlerts },
  );
  const dismiss = useUpdateBillingReconciliationLifecycleAlert();
  const [expanded, setExpanded] = useState(Boolean(context?.source === 'lifecycle'));
  const [pendingDismiss, setPendingDismiss] = useState(null);
  const [dismissReason, setDismissReason] = useState('');
  const [locallyDismissedIds, setLocallyDismissedIds] = useState(() => new Set());
  useEffect(() => {
    setLocallyDismissedIds(new Set());
  }, [bac, runId, prefetchedAlerts]);
  const alerts = useMemo(() => {
    const sourceAlerts = hasPrefetchedAlerts ? prefetchedAlerts : (query.data || []);
    return sortedAlerts(sourceAlerts.filter((alert) => !locallyDismissedIds.has(alert.id)));
  }, [hasPrefetchedAlerts, locallyDismissedIds, prefetchedAlerts, query.data]);

  if (!hasPrefetchedAlerts && (query.isLoading || query.isFetching)) {
    return (
      <Stack spacing={0.5}>
        <Skeleton height={34} />
        <Skeleton height={34} />
      </Stack>
    );
  }
  if (!hasPrefetchedAlerts && query.isError) return <Alert severity="warning">Lifecycle alerts for this BAC could not be loaded.</Alert>;
  if (!alerts.length) return null;

  const errors = alerts.filter((alert) => alert.severity === 'error').length;
  const severity = errors ? 'error' : 'warning';
  const submitDismiss = () => {
    const reason = dismissReason.trim();
    if (!pendingDismiss || !reason) return;
    dismiss.mutate(
      { id: pendingDismiss.id, runId, bac, patch: { dismissed: true, dismissed_reason: reason } },
      {
        onSuccess: () => {
          setLocallyDismissedIds((current) => new Set([...current, pendingDismiss.id]));
          setPendingDismiss(null);
          setDismissReason('');
        },
      },
    );
  };

  return (
    <>
      <Accordion
        disableGutters
        expanded={expanded}
        onChange={(_event, nextExpanded) => setExpanded(nextExpanded)}
        variant="outlined"
        sx={{
          borderColor: severity === 'error' ? 'error.light' : 'warning.light',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ minWidth: 0 }}>
            <NotificationsActiveOutlinedIcon color={severityColor(severity)} fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              Lifecycle alerts for this BAC
            </Typography>
            <Chip size="small" label={`${alerts.length} active`} color={severityColor(severity)} variant="outlined" />
            {errors > 0 ? <Chip size="small" label={`${errors} error${errors === 1 ? '' : 's'}`} color="error" /> : null}
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Stack spacing={0.75}>
            {alerts.map((alert) => (
              <Stack
                key={alert.id}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{
                  p: 1.25,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: (theme) => (theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.035)'
                    : 'rgba(15, 23, 42, 0.025)'),
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                    <Chip size="small" color={severityColor(alert.severity)} label={alert.severity || 'info'} />
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {lifecycleAlertLabel(alert.code)}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                    {alertDealerName(alert) || `BAC ${alert.bac || bac}`}
                  </Typography>
                  {alert.details?.summary ? (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {alert.details.summary}
                    </Typography>
                  ) : null}
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={dismiss.isPending}
                  onClick={() => {
                    setPendingDismiss(alert);
                    setDismissReason('');
                  }}
                  sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, textTransform: 'none' }}
                >
                  Dismiss
                </Button>
              </Stack>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
      <Dialog open={Boolean(pendingDismiss)} onClose={() => setPendingDismiss(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Dismiss lifecycle alert</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Alert severity={severityColor(pendingDismiss?.severity || 'info')}>
              This dismisses the lifecycle alert for this BAC only.
            </Alert>
            <TextField
              autoFocus
              label="Dismissal reason"
              value={dismissReason}
              onChange={(event) => setDismissReason(event.target.value)}
              multiline
              minRows={3}
              fullWidth
              required
              inputProps={{ 'aria-label': 'Dismissal reason' }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDismiss(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitDismiss}
            disabled={!dismissReason.trim() || dismiss.isPending}
          >
            Dismiss alert
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LifecycleAlertsDrawerSummary;
